import { useEffect, useState } from "react";

/**
 * Sticky filter bar on mobile uses `top: headerHeight`, measure the real header
 * (alerts, dismiss, etc.) and track desktop breakpoint.
 */
export function useStickySearchChrome() {
  const [headerHeight, setHeaderHeight] = useState(160);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    let rafId = 0;
    let resizeObserver: ResizeObserver | null = null;

    const updateHeaderHeight = (forceDesktopHeight = false) => {
      if (typeof window === "undefined") return;

      if (forceDesktopHeight || window.innerWidth >= 1024) {
        setHeaderHeight(200);
        return;
      }

      const header = document.querySelector("header.sticky");
      if (header) {
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          const height = header.getBoundingClientRect().height;
          // Avoid noisy sub-pixel state churn that can cause sticky flicker.
          setHeaderHeight(Math.max(0, Math.round(height)));
        });
      }
    };

    updateHeaderHeight();

    const header = document.querySelector("header.sticky");
    if (header && "ResizeObserver" in window) {
      resizeObserver = new ResizeObserver(() => {
        updateHeaderHeight();
      });
      resizeObserver.observe(header);
    }

    const onResize = () => updateHeaderHeight();
    const onOrientationChange = () => {
      // Layout settles one frame after orientation flip on mobile browsers.
      updateHeaderHeight();
      setTimeout(() => updateHeaderHeight(), 120);
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onOrientationChange);

    const viewport = window.visualViewport;
    const onViewportResize = () => updateHeaderHeight();
    viewport?.addEventListener("resize", onViewportResize);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onOrientationChange);
      viewport?.removeEventListener("resize", onViewportResize);
      resizeObserver?.disconnect();
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const apply = () => setIsDesktop(mq.matches);
    apply();
    if (mq.addEventListener) mq.addEventListener("change", apply);
    else mq.addListener?.(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", apply);
      else mq.removeListener?.(apply);
    };
  }, []);

  return { headerHeight, isDesktop };
}
